import { z } from "zod";
import { deletedResponseSchema, firestoreDocument, isoDateSchema, timeStringSchema, updatedResponseSchema, } from "./common.js";
export const businessHoursEntrySchema = z
    .object({
    dayOfWeek: z.number().int().min(0).max(6),
    closed: z.boolean(),
    openTime: timeStringSchema.nullable(),
    closeTime: timeStringSchema.nullable(),
    breakStartTime: timeStringSchema.nullable().optional(),
    breakEndTime: timeStringSchema.nullable().optional(),
    staffCount: z.number().int().min(0).max(50).nullable().optional(),
})
    .refine((v) => v.closed || (v.openTime !== null && v.closeTime !== null), "Apningstid kreves når åpent")
    .refine((v) => v.closed || v.openTime < v.closeTime, "Stengetid må være etter åpningstid")
    .refine((v) => v.closed ||
    (!v.breakStartTime && !v.breakEndTime) ||
    (!!v.breakStartTime &&
        !!v.breakEndTime &&
        v.openTime < v.breakStartTime &&
        v.breakStartTime < v.breakEndTime &&
        v.breakEndTime < v.closeTime), "Lunsj må ligge innenfor åpningstiden");
export const manifestDateOverrideSchema = z
    .object({
    date: isoDateSchema,
    closed: z.boolean(),
    openTime: timeStringSchema.nullable(),
    closeTime: timeStringSchema.nullable(),
    breakStartTime: timeStringSchema.nullable().optional(),
    breakEndTime: timeStringSchema.nullable().optional(),
    staffCount: z.number().int().min(0).max(50).nullable(),
    reason: z.string().max(120).nullable(),
})
    .refine((v) => v.closed || (v.openTime !== null && v.closeTime !== null), "Apningstid kreves når åpent")
    .refine((v) => v.closed || v.openTime < v.closeTime, "Stengetid må være etter åpningstid")
    .refine((v) => v.closed ||
    (!v.breakStartTime && !v.breakEndTime) ||
    (!!v.breakStartTime &&
        !!v.breakEndTime &&
        v.openTime < v.breakStartTime &&
        v.breakStartTime < v.breakEndTime &&
        v.breakEndTime < v.closeTime), "Lunsj må ligge innenfor åpningstiden");
export const bookingTypeConfigSchema = z.object({
    enabled: z.boolean(),
    label: z.string().min(1).max(60),
    durationMinutes: z.number().int().min(15).max(180),
    description: z.string().max(500),
    stations: z.number().int().min(1).max(20),
    priceNok: z.number().int().min(0).max(100_000).default(0),
});
const slotIntervalSchema = z
    .number()
    .int()
    .min(5)
    .max(180)
    .refine((v) => v % 5 === 0, "Må være delelig på 5");
// Create and update take the same body; they stay separate names so each route reads as itself.
const manifestPayloadSchema = z.object({
    name: z.string().min(1).max(80),
    description: z.string().max(500).nullable().optional(),
    active: z.boolean(),
    slotIntervalMinutes: slotIntervalSchema,
    staffCount: z.number().int().min(1).max(50),
    bookingHorizonDays: z.number().int().min(1).max(365).optional(),
    minNoticeMinutes: z.number().int().min(0).max(20160).optional(),
    requireLogin: z.boolean().optional(),
    types: z.object({
        VERDIVURDERING: bookingTypeConfigSchema,
        SALG: bookingTypeConfigSchema,
        ANALYSE: bookingTypeConfigSchema,
    }),
    businessHours: z.array(businessHoursEntrySchema).length(7),
    dateOverrides: z.array(manifestDateOverrideSchema).max(365).optional(),
});
export const createManifestSchema = manifestPayloadSchema;
export const updateManifestSchema = manifestPayloadSchema;
export const createBlockedSlotSchema = z
    .object({
    startAt: z.number().int().positive(),
    endAt: z.number().int().positive(),
    reason: z.string().max(200).optional(),
})
    .refine((v) => v.endAt > v.startAt, "Slutt må være etter start");
export const updateVisionPointStoreConfigSchema = z.object({
    enabled: z.boolean(),
    baseUrl: z.string().trim().max(500).optional(),
    username: z.string().trim().max(200).optional(),
    password: z.string().max(1000).optional(),
    actorCode: z.string().trim().max(80).optional(),
    interactionCustomerNo: z.string().trim().max(80).optional(),
    serviceItemNo: z.string().trim().max(80).optional(),
    industryCode: z.string().trim().max(120).optional(),
    initialStatusCode: z.string().trim().max(80).optional(),
});
export const visionPointStoreConfigRedactedSchema = z.object({
    id: z.string(),
    manifestId: z.string(),
    enabled: z.boolean(),
    baseUrl: z.string(),
    username: z.string(),
    actorCode: z.string(),
    interactionCustomerNo: z.string(),
    serviceItemNo: z.string(),
    industryCode: z.string(),
    initialStatusCode: z.string(),
    updatedByUid: z.string().nullable(),
    hasPassword: z.boolean(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const createManifestResponseSchema = firestoreDocument();
export const updateManifestResponseSchema = updatedResponseSchema;
export const deleteManifestResponseSchema = deletedResponseSchema;
export const visionPointConfigResponseSchema = visionPointStoreConfigRedactedSchema.nullable();
/** What VisionPoint says about the account a store's saved credentials log in to. */
export const visionPointConnectionCheckSchema = z.object({
    /** The VisionPoint user the credentials belong to. */
    userName: z.string().nullable(),
    /** The company the account belongs to, e.g. "NO 987 654 321 MVA". */
    companyId: z.string().nullable(),
    /** The store the account is tied to. */
    storeId: z.string().nullable(),
    /** The till the account is tied to. */
    posId: z.string().nullable(),
    /** When the check ran, epoch ms. */
    checkedAt: z.number(),
});
export const createBlockedSlotResponseSchema = z.object({ id: z.string() });
export const deleteBlockedSlotResponseSchema = z.object({ removed: z.literal(true) });
